import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Rocket } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import type {
  DeploymentArtifact,
  DeploymentArtifactStatus,
  ReleaseDeployment,
  ReleaseDetail,
} from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Drawer,
  EmptyState,
  Field,
  LoadingState,
  StatusBadge,
  Textarea,
} from "../../shared/ui";
import { DeploymentDrawer } from "./DeploymentDrawer";
import {
  DEPLOYMENT_STATUS_LABELS,
  DEPLOYMENT_STATUS_TONE,
  describeAttempt,
} from "./releaseVocabulary";
import { formatReleaseMoment } from "./releaseVocabulary";

const ARTIFACT_LABELS: Record<DeploymentArtifactStatus, string> = {
  IN_PROGRESS: "going out",
  SUCCEEDED: "landed",
  FAILED: "failed",
  SKIPPED: "skipped",
  ROLLED_BACK: "rolled back",
};

const ARTIFACT_DOT: Record<DeploymentArtifactStatus, string> = {
  IN_PROGRESS: "bg-amber-400",
  SUCCEEDED: "bg-green-500",
  FAILED: "bg-red-500",
  SKIPPED: "bg-ink-300",
  ROLLED_BACK: "bg-violet-400",
};

/**
 * Every attempt to put this release somewhere (§62).
 *
 * <p>Kept as a list of attempts rather than a status per environment. A release that reached
 * production on the third try is a different release from one that reached it on the first, and
 * the only place that difference survives is here.
 */
export function ReleaseDeploymentsCard({
  release,
  onChanged,
}: {
  release: ReleaseDetail;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const [finishing, setFinishing] = useState<ReleaseDeployment | null>(null);
  const [starting, setStarting] = useState(false);
  const [settling, setSettling] = useState<string | null>(null);
  const [failing, setFailing] = useState<{
    deploymentId: string;
    artifact: DeploymentArtifact;
  } | null>(null);
  const [failureReason, setFailureReason] = useState("");

  /**
   * Records how one module fared, leaving the attempt alone.
   *
   * <p>Separate acts: a night ends once, the things it carried end one at a time. Somebody
   * watching the third fail needs to write that down while the fourth is still running.
   */
  const settle = async (
    deploymentId: string,
    artifact: DeploymentArtifact,
    status: DeploymentArtifactStatus,
    result?: string,
  ) => {
    setSettling(artifact.id);
    try {
      await api.put(
        `/api/v1/releases/${release.id}/deployments/${deploymentId}/modules/${artifact.id}`,
        { status, result: result ?? null, lockVersion: artifact.lockVersion },
      );
      onChanged();
    } finally {
      setSettling(null);
    }
  };

  const deployments = useQuery({
    queryKey: ["release-deployments", release.id],
    queryFn: () => api.get<ReleaseDeployment[]>(`/api/v1/releases/${release.id}/deployments`),
  });

  const rows = [...(deployments.data ?? [])].sort((a, b) =>
    (b.startedAt ?? "").localeCompare(a.startedAt ?? ""),
  );
  const running = rows.filter((row) => row.status === "IN_PROGRESS");

  return (
    <Card>
      <CardHeader
        title="Deployments"
        subtitle={
          rows.length === 0
            ? "Nothing deployed yet."
            : `${rows.length} ${rows.length === 1 ? "attempt" : "attempts"}${
                running.length > 0 ? ` · ${running.length} running` : ""
              }`
        }
        action={
          can("RELEASE_EXECUTE") &&
          rows.length > 0 && (
            <Button
              size="sm"
              icon={<Rocket className="h-4 w-4" />}
              onClick={() => setStarting(true)}
            >
              Start a deployment
            </Button>
          )
        }
      />
      <CardBody>
        {deployments.isPending ? (
          <LoadingState label="Loading deployments…" />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No deployments recorded"
            message="Each attempt is recorded separately, so a retry never hides the one that failed."
            action={
              can("RELEASE_EXECUTE") ? (
                <Button onClick={() => setStarting(true)}>Start a deployment</Button>
              ) : undefined
            }
          />
        ) : (
          <ul className="divide-y divide-ink-100">
            {rows.map((row) => (
              <li key={row.id} className="flex items-start justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink-900">
                    {describeAttempt(row.attemptNo, row.environment.name)}
                  </p>
                  <p className="mt-0.5 text-xs text-ink-500">
                    {formatReleaseMoment(row.startedAt, release.timeZone)}
                    {row.startedBy && ` · ${row.startedBy.fullName}`}
                    {row.artifactVersion && ` · ${row.artifactVersion}`}
                  </p>
                  {row.pipelineRunReference && (
                    <p className="mt-0.5 font-mono text-xs text-ink-500">
                      {row.pipelineRunReference}
                    </p>
                  )}
                  {row.result && (
                    <p
                      className={`mt-1 rounded-md border-l-2 px-2 py-1 text-xs ${
                        row.status === "FAILED"
                          ? "border-red-300 bg-red-50 text-red-800"
                          : "border-ink-200 bg-ink-50 text-ink-700"
                      }`}
                    >
                      {row.result}
                    </p>
                  )}

                  {/* What the attempt carried, module by module. This is what makes "which
                      version of Accounting is live here?" answerable — the manifest says what
                      was planned, only this says what arrived. */}
                  {row.artifacts.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {row.artifacts.map((carried) => (
                        <li key={carried.id} className="flex items-center gap-2 text-xs">
                          <span
                            className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                              ARTIFACT_DOT[carried.status]
                            }`}
                            aria-hidden
                          />
                          <span className="text-ink-800">{carried.module.name}</span>
                          <span className="tnum font-mono text-ink-600">
                            {carried.moduleVersion ?? "—"}
                          </span>
                          <span className="text-ink-500">
                            {ARTIFACT_LABELS[carried.status]}
                          </span>
                          {carried.result && (
                            <span className="truncate text-ink-500">· {carried.result}</span>
                          )}
                          {/* Each module settles on its own — the attempt cannot be called
                              succeeded until they have, and without these there was no way to
                              say so. */}
                          {can("RELEASE_EXECUTE") && carried.status === "IN_PROGRESS" && (
                            <span className="ml-auto flex shrink-0 gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                loading={settling === carried.id}
                                onClick={() => void settle(row.id, carried, "SUCCEEDED")}
                              >
                                Landed
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setFailing({ deploymentId: row.id, artifact: carried })}
                              >
                                Failed
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                loading={settling === carried.id}
                                onClick={() => void settle(row.id, carried, "SKIPPED")}
                              >
                                Skip
                              </Button>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <StatusBadge tone={DEPLOYMENT_STATUS_TONE[row.status]}>
                    {DEPLOYMENT_STATUS_LABELS[row.status]}
                  </StatusBadge>
                  {can("RELEASE_EXECUTE") && row.status === "IN_PROGRESS" && (
                    <Button variant="ghost" size="sm" onClick={() => setFinishing(row)}>
                      Record outcome
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardBody>

      {/* A failed module always says what went wrong. It is the first thing anybody reads the
          next morning, and a blank there is exactly where the question is. */}
      <Drawer
        open={failing != null}
        onClose={() => {
          setFailing(null);
          setFailureReason("");
        }}
        title="What went wrong?"
        subtitle={failing ? `${failing.artifact.module.name} ${failing.artifact.moduleVersion ?? ""}` : undefined}
        footer={
          <div className="flex justify-between gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setFailing(null);
                setFailureReason("");
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={failureReason.trim() === ""}
              loading={settling === failing?.artifact.id}
              onClick={() => {
                if (!failing) {
                  return;
                }
                void settle(
                  failing.deploymentId,
                  failing.artifact,
                  "FAILED",
                  failureReason.trim(),
                ).then(() => {
                  setFailing(null);
                  setFailureReason("");
                });
              }}
            >
              Record the failure
            </Button>
          </div>
        }
      >
        <Field
          label="What happened"
          required
          hint="The other modules in this attempt are unaffected — only this one is being marked failed."
        >
          <Textarea
            rows={4}
            autoFocus
            value={failureReason}
            placeholder="Migration V37 timed out on stock_movement."
            onChange={(event) => setFailureReason(event.target.value)}
          />
        </Field>
      </Drawer>

      <DeploymentDrawer
        release={release}
        deployment={finishing}
        open={starting || finishing != null}
        onClose={() => {
          setStarting(false);
          setFinishing(null);
        }}
        onSaved={onChanged}
      />
    </Card>
  );
}
